/**
 * "Chuyển tên miền về" for a customer (OT&E finding F10): the price on the customer's account, the
 * registrant (one of the customer's subjects, checked the same way as a registration before any
 * money is held), the transfer code from the other registrar, then the order and the transfer —
 * the same steps as a customer transferring a domain in themselves. Missing registrant details
 * are listed by field. The transfer code is sent once and never shown again. A TLD that asks for
 * more than the registrant's usual details shows its fields, as in "Đăng ký tên miền".
 */

import { ArrowDownToLine } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api, isApiError, type RegistrantProblem } from '../../api';
import type { CustomerSummary } from '../../api/areas/accounts';
import type { RenewQuote } from '../../api/areas/core';
import { Callout, Field } from '../../components/blocks';
import { useWrite, WriteError } from '../../components/Write';
import { initialValues, missingKeys, valuesToSend } from '../../lib/extraFields';
import { formatMoney } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { ExtraFieldInputs, useTldExtraFields } from '../domains/ExtraFields';
import { RegistrantIncomplete, RegistrantReadiness } from '../domains/RegistrationStop';
import { CustomerPicker } from './RegisterDomain';
import { ActionDialog, FormFooter, ReasonField, selectClass, type DialogBodyProps } from './shared';

type TransferQuote = RenewQuote & { domain: string; unicode: string };

function TransferInForm({ close, changed, initialDomain }: DialogBodyProps & { initialDomain: string }) {
  const { t } = useTranslation('operator');
  const [customer, setCustomer] = useState<CustomerSummary | null>(null);
  const [domain, setDomain] = useState(initialDomain);
  const [subject, setSubject] = useState('');
  const [authInfo, setAuthInfo] = useState('');
  const [agreement, setAgreement] = useState('');
  const [reason, setReason] = useState('');
  const [quote, setQuote] = useState<TransferQuote | null>(null);
  const [extra, setExtra] = useState<Record<string, string>>({});
  const subjects = useApi(
    (signal) => (customer ? api.listSubjects({ customer_id: customer.id, limit: 100 }, { signal }) : Promise.resolve({ items: [], next_after: null })),
    [customer?.id],
  );
  const pricing = useWrite((b: { customer_id: string; domain: string; subject_id?: string | null }, opts) => api.quoteTransferIn(b, opts));
  const order = useWrite((b: Parameters<typeof api.transferIn>[0], opts) => api.transferIn(b, opts));
  const placed = order.state.phase === 'done' ? order.state.result : null;
  const subjectItems = subjects.status === 'success' ? subjects.data.items : [];
  // The extra data the TLD requires (none for most TLDs), for the chosen registrant.
  const zone = quote ? quote.domain.split('.').slice(1).join('.') || null : null;
  const extraSpec = useTldExtraFields(zone, subject);
  const extraValues: Record<string, string> = extraSpec
    ? { ...initialValues(extraSpec.fields), ...Object.fromEntries(Object.entries(extra).filter(([k, v]) => v.trim() && extraSpec.fields.some((f) => f.key === k))) }
    : {};
  const extraMissing = extraSpec ? missingKeys(extraSpec.fields, extraValues) : [];
  const orderError = order.state.phase === 'error' ? order.state.error : null;
  const orderProblems: RegistrantProblem[] =
    isApiError(orderError) && orderError.code === 'REGISTRANT_INCOMPLETE' ? ((orderError.details as { fields?: RegistrantProblem[] } | null)?.fields ?? []) : [];
  const priceIt = () => {
    if (!customer || !domain.trim()) return;
    void pricing.run({ customer_id: customer.id, domain: domain.trim(), subject_id: subject || null }).then((q) => setQuote(q ?? null));
  };
  const ready = Boolean(customer && quote && subject && authInfo.trim() && agreement.trim() && reason.trim()) && extraMissing.length === 0 && !placed;
  return (
    <form
      className="grid gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (!customer || !quote) return;
        void order
          .run({
            customer_id: customer.id,
            quote_id: quote.quote_id,
            subject_id: subject,
            auth_info: authInfo,
            accepted_agreement_version: agreement,
            reason,
            ...(extraSpec ? { extra_fields: valuesToSend(extraValues) } : {}),
          })
          .then((r) => {
            if (r) {
              setAuthInfo('');
              changed();
            }
          });
      }}
    >
      <Field label={t('transferIn.customer')} group>
        <CustomerPicker
          value={customer}
          onChange={(c) => {
            setCustomer(c);
            setSubject('');
            setQuote(null);
          }}
        />
      </Field>
      <Field label={t('transferIn.domain')} hint={t('transferIn.domainHint')}>
        <div className="flex gap-2">
          <Input
            value={domain}
            onChange={(e) => {
              setDomain(e.target.value);
              setQuote(null);
            }}
            placeholder="example.com"
            autoComplete="off"
          />
          <Button type="button" variant="outline" disabled={!customer || !domain.trim() || pricing.busy} onClick={priceIt}>
            {t('transferIn.price')}
          </Button>
        </div>
      </Field>
      {pricing.state.phase === 'error' ? <WriteError error={pricing.state.error} /> : null}
      {pricing.state.phase === 'error' ? <RegistrantIncomplete error={pricing.state.error} /> : null}
      {quote && customer ? <p className="text-sm">{t('transferIn.quote', { domain: quote.unicode || quote.domain, total: formatMoney(quote.total as Parameters<typeof formatMoney>[0]), customer: customer.display_name })}</p> : null}
      {customer ? (
        <Field label={t('transferIn.registrant')} hint={t('transferIn.registrantHint')}>
          {subjectItems.length === 0 && subjects.status === 'success' ? (
            <Callout tone="warn">{t('transferIn.noRegistrant')}</Callout>
          ) : (
            <select className={selectClass} value={subject} onChange={(e) => setSubject(e.target.value)} required>
              <option value="">{t('common.choose')}</option>
              {subjectItems.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.legal_name ?? s.id}
                </option>
              ))}
            </select>
          )}
        </Field>
      ) : null}
      {subject ? <RegistrantReadiness subjectId={subject} domain={quote?.domain ?? domain} /> : null}
      {extraSpec ? <ExtraFieldInputs spec={extraSpec} values={extraValues} onChange={(k, v) => setExtra((x) => ({ ...x, [k]: v }))} problems={orderProblems} /> : null}
      <Field label={t('transferIn.authInfo')} hint={t('transferIn.authInfoHint')}>
        <Input type="password" value={authInfo} onChange={(e) => setAuthInfo(e.target.value)} maxLength={64} autoComplete="off" />
      </Field>
      <Field label={t('register.agreement')} hint={t('register.agreementHint')}>
        <Input value={agreement} onChange={(e) => setAgreement(e.target.value)} maxLength={64} />
      </Field>
      <ReasonField value={reason} onChange={setReason} />
      {order.state.phase === 'error' ? <WriteError error={order.state.error} /> : null}
      {order.state.phase === 'error' ? <RegistrantIncomplete error={order.state.error} hideExtra={Boolean(extraSpec)} /> : null}
      {placed ? <Callout tone="ok">{t('transferIn.started')}</Callout> : null}
      <FormFooter busy={order.busy} disabled={!ready} submit={placed ? undefined : t('transferIn.submit')} onClose={close} />
    </form>
  );
}

/** "Chuyển tên miền về" button with its dialog; `domain` prefills the name (Ctrl-K "chuyển về x"). */
export function TransferInButton({ domain = '', ...control }: { domain?: string; open?: boolean; onOpenChange?: (open: boolean) => void; trigger?: boolean }) {
  const { t } = useTranslation('operator');
  return (
    <ActionDialog label={t('transferIn.button')} icon={ArrowDownToLine} title={t('transferIn.title')} description={t('transferIn.description')} wide {...control}>
      {(p) => <TransferInForm {...p} initialDomain={domain} />}
    </ActionDialog>
  );
}
